import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import {
  Plus,
  Trash2,
  Pencil,
  Check,
  Circle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ListTodo,
  AlertCircle,
  Calendar,
} from 'lucide-react';
import {
  Card,
  CardHeader,
  CardContent,
  Button,
  Input,
  Select,
  Modal,
  LoadingSpinner,
} from '../components/ui';
import {
  getTodos,
  addTodo,
  updateTodo,
  toggleTodo,
  deleteTodo,
} from '../services/api';
import type { Todo, TodoFormData } from '../types';

const priorityOptions = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
];

const priorityColors: Record<string, string> = {
  low: 'text-blue-400 bg-blue-500/10 border-blue-500/30',
  medium: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/30',
  high: 'text-red-400 bg-red-500/10 border-red-500/30',
};

const getInitialFormData = (): TodoFormData => ({
  title: '',
  description: '',
  priority: 'medium',
  dueDate: null,
});

type FilterStatus = 'all' | 'active' | 'completed';

export default function Tools() {
  const [loading, setLoading] = useState(true);
  const [todos, setTodos] = useState<Todo[]>([]);
  const [filter, setFilter] = useState<FilterStatus>('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTodo, setSelectedTodo] = useState<Todo | null>(null);
  const [formData, setFormData] = useState<TodoFormData>(getInitialFormData());
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchTodos();
  }, []);

  const fetchTodos = async () => {
    try {
      setLoading(true);
      const res = await getTodos();
      if (res.success) {
        setTodos(res.todos);
      } else {
        setError(res.error || 'Failed to fetch todos');
      }
    } catch (err) {
      setError(String(err));
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!formData.title.trim()) return;
    try {
      setSaving(true);
      const res = await addTodo(formData);
      if (res.success && res.todo) {
        setTodos((prev) => [res.todo!, ...prev]);
        setShowAddModal(false);
        setFormData(getInitialFormData());
      } else {
        setError(res.error || 'Failed to add todo');
      }
    } catch (err) {
      setError(String(err));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedTodo || !formData.title.trim()) return;
    try {
      setSaving(true);
      const res = await updateTodo(selectedTodo.id, formData);
      if (res.success && res.todo) {
        setTodos((prev) => prev.map((t) => (t.id === selectedTodo.id ? res.todo! : t)));
        setShowEditModal(false);
        setSelectedTodo(null);
        setFormData(getInitialFormData());
      } else {
        setError(res.error || 'Failed to update todo');
      }
    } catch (err) {
      setError(String(err));
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (id: string) => {
    try {
      const res = await toggleTodo(id);
      if (res.success && res.todo) {
        setTodos((prev) => prev.map((t) => (t.id === id ? res.todo! : t)));
      }
    } catch (err) {
      setError(String(err));
    }
  };

  const handleDelete = async () => {
    if (!selectedTodo) return;
    try {
      setSaving(true);
      const res = await deleteTodo(selectedTodo.id);
      if (res.success) {
        setTodos((prev) => prev.filter((t) => t.id !== selectedTodo.id));
        setShowDeleteModal(false);
        setSelectedTodo(null);
      } else {
        setError(res.error || 'Failed to delete todo');
      }
    } catch (err) {
      setError(String(err));
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (todo: Todo) => {
    setSelectedTodo(todo);
    setFormData({
      title: todo.title,
      description: todo.description,
      priority: todo.priority,
      dueDate: todo.dueDate,
    });
    setShowEditModal(true);
  };

  const openDelete = (todo: Todo) => {
    setSelectedTodo(todo);
    setShowDeleteModal(true);
  };

  const filteredTodos = todos.filter((t) => {
    if (filter === 'active') return !t.completed;
    if (filter === 'completed') return t.completed;
    return true;
  });

  const activeCount = todos.filter((t) => !t.completed).length;
  const completedCount = todos.filter((t) => t.completed).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <ListTodo className="w-7 h-7 text-primary-400" />
            Todo
          </h1>
          <p className="text-dark-400 mt-1">Keep track of your tasks</p>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-center gap-2 text-red-400">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-300">×</button>
        </div>
      )}

      {/* Todo Section */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-2">
              <ListTodo className="w-5 h-5 text-primary-400" />
              <h2 className="text-lg font-semibold text-white">Todo List</h2>
              <span className="text-xs text-dark-400 ml-2">
                {activeCount} active · {completedCount} done
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex bg-dark-700 rounded-lg p-0.5">
                {(['all', 'active', 'completed'] as FilterStatus[]).map((f) => (
                  <button
                    key={f}
                    onClick={() => setFilter(f)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors capitalize ${
                      filter === f
                        ? 'bg-primary-500 text-white'
                        : 'text-dark-400 hover:text-white'
                    }`}
                  >
                    {f}
                  </button>
                ))}
              </div>
              <Button
                size="sm"
                onClick={() => {
                  setFormData(getInitialFormData());
                  setShowAddModal(true);
                }}
              >
                <Plus className="w-4 h-4 mr-1" />
                Add
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {filteredTodos.length === 0 ? (
            <div className="text-center py-12 text-dark-400">
              <ListTodo className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm">
                {filter === 'all'
                  ? 'No todos yet. Add one to get started!'
                  : `No ${filter} todos.`}
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-dark-700">
              {filteredTodos.map((todo) => (
                <li
                  key={todo.id}
                  className="group hover:bg-dark-750 transition-colors"
                >
                  <div className="flex items-start gap-3 px-4 sm:px-6 py-3">
                    {/* Toggle */}
                    <button
                      onClick={() => handleToggle(todo.id)}
                      className="mt-0.5 flex-shrink-0"
                    >
                      {todo.completed ? (
                        <CheckCircle2 className="w-5 h-5 text-green-400" />
                      ) : (
                        <Circle className="w-5 h-5 text-dark-500 hover:text-primary-400 transition-colors" />
                      )}
                    </button>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className={`text-sm font-medium ${
                            todo.completed
                              ? 'line-through text-dark-500'
                              : 'text-white'
                          }`}
                        >
                          {todo.title}
                        </span>
                        <span
                          className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded border ${
                            priorityColors[todo.priority]
                          }`}
                        >
                          {todo.priority}
                        </span>
                        {todo.dueDate && (
                          <span className="text-[11px] text-dark-400 flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {format(new Date(todo.dueDate), 'MMM d, yyyy')}
                          </span>
                        )}
                      </div>

                      {/* Description (expandable) */}
                      {todo.description && (
                        <>
                          <button
                            onClick={() =>
                              setExpandedId(expandedId === todo.id ? null : todo.id)
                            }
                            className="text-xs text-dark-500 hover:text-dark-300 mt-1 flex items-center gap-1"
                          >
                            {expandedId === todo.id ? (
                              <>
                                <ChevronUp className="w-3 h-3" /> Hide details
                              </>
                            ) : (
                              <>
                                <ChevronDown className="w-3 h-3" /> Show details
                              </>
                            )}
                          </button>
                          {expandedId === todo.id && (
                            <p className="text-xs text-dark-400 mt-1 whitespace-pre-wrap">
                              {todo.description}
                            </p>
                          )}
                        </>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                      <button
                        onClick={() => openEdit(todo)}
                        className="p-1.5 rounded-lg text-dark-400 hover:text-primary-400 hover:bg-dark-700 transition-colors"
                        title="Edit"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => openDelete(todo)}
                        className="p-1.5 rounded-lg text-dark-400 hover:text-red-400 hover:bg-dark-700 transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Add Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add Todo"
        size="md"
      >
        <div className="space-y-4">
          <Input
            label="Title"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            placeholder="What needs to be done?"
            autoFocus
          />
          <div>
            <label className="block text-sm font-medium text-dark-300 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              placeholder="Optional details..."
              rows={3}
              className="w-full bg-dark-700 border border-dark-600 rounded-lg px-3 py-2 text-white text-sm placeholder-dark-500 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 resize-none"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Select
              label="Priority"
              value={formData.priority}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  priority: e.target.value as 'low' | 'medium' | 'high',
                })
              }
              options={priorityOptions}
            />
            <Input
              label="Due Date"
              type="date"
              value={formData.dueDate || ''}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  dueDate: e.target.value || null,
                })
              }
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="secondary"
              onClick={() => setShowAddModal(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleAdd} disabled={saving || !formData.title.trim()}>
              {saving ? <LoadingSpinner size="sm" /> : <Check className="w-4 h-4 mr-1" />}
              Add Todo
            </Button>
          </div>
        </div>
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedTodo(null);
        }}
        title="Edit Todo"
        size="md"
      >
        <div className="space-y-4">
          <Input
            label="Title"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            placeholder="What needs to be done?"
            autoFocus
          />
          <div>
            <label className="block text-sm font-medium text-dark-300 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              placeholder="Optional details..."
              rows={3}
              className="w-full bg-dark-700 border border-dark-600 rounded-lg px-3 py-2 text-white text-sm placeholder-dark-500 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 resize-none"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Select
              label="Priority"
              value={formData.priority}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  priority: e.target.value as 'low' | 'medium' | 'high',
                })
              }
              options={priorityOptions}
            />
            <Input
              label="Due Date"
              type="date"
              value={formData.dueDate || ''}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  dueDate: e.target.value || null,
                })
              }
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="secondary"
              onClick={() => {
                setShowEditModal(false);
                setSelectedTodo(null);
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleUpdate} disabled={saving || !formData.title.trim()}>
              {saving ? <LoadingSpinner size="sm" /> : <Check className="w-4 h-4 mr-1" />}
              Save Changes
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedTodo(null);
        }}
        title="Delete Todo"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-dark-300 text-sm">
            Are you sure you want to delete{' '}
            <span className="text-white font-medium">
              "{selectedTodo?.title}"
            </span>
            ? This action cannot be undone.
          </p>
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                setShowDeleteModal(false);
                setSelectedTodo(null);
              }}
            >
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete} disabled={saving}>
              {saving ? <LoadingSpinner size="sm" /> : <Trash2 className="w-4 h-4 mr-1" />}
              Delete
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
